import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { admin, ApiError, errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { date } from "../lib/format";
import type { Company, Domain, List, PageRevision, Store, StorePage, StorePageDetail } from "../lib/types";
import { Alert, Badge, Button, Card, Field, Modal, PageHeader, Spinner, Table, Toggle } from "../components/ui";
import Markdown, { fill } from "../components/Markdown";

// Câmpurile completate automat în text, din Setări → Date firmă.
const PLACEHOLDERS: [string, string][] = [
  ["magazin", "numele magazinului"], ["firma", "denumirea firmei"], ["cui", "CUI"], ["reg_com", "nr. Reg. Com."],
  ["sediu", "sediul social"], ["email", "email"], ["telefon", "telefon"],
];

function useCanEdit() {
  const { me } = useAuth();
  return me?.user?.role === "owner" || me?.user?.role === "admin";
}

// Adresa paginii în magazin: domeniul principal în producție, magazinul local implicit în dezvoltare.
function useShopUrl() {
  const domains = useQuery({ queryKey: ["domains"], queryFn: () => admin.get<List<Domain>>("/domains") });
  const host = domains.data?.data.find((d) => d.is_primary)?.host ?? domains.data?.data[0]?.host;
  return (slug: string) => (!host ? undefined : location.protocol === "https:" ? `https://${host}/pagina/${slug}` : `http://localhost:3100/pagina/${slug}`);
}

function CompanyNotice() {
  const store = useQuery({ queryKey: ["store"], queryFn: () => admin.get<Store>("/store") });
  const c = (store.data?.settings.company ?? {}) as Company;
  if (!store.data || (c.legal_name && c.vat_number && c.reg_number && c.address && c.email)) return null;
  return (
    <div className="mb-6">
      <Alert tone="amber">
        Datele firmei nu sunt complete: în magazin apare „[necompletat]” în locul lor. Completează-le în{" "}
        <Link to="/settings" className="font-semibold underline">Setări → Date firmă</Link>.
      </Alert>
    </div>
  );
}

export default function Pages() {
  const canEdit = useCanEdit();
  const list = useQuery({ queryKey: ["pages"], queryFn: () => admin.get<List<StorePage>>("/pages") });
  const url = useShopUrl();
  return (
    <>
      <PageHeader
        title="Pagini"
        subtitle="Paginile legale ale magazinului și paginile tale. Apar în subsolul magazinului."
        actions={canEdit && <Link to="/pages/new"><Button>+ Pagină</Button></Link>}
      />
      <CompanyNotice />
      {list.isLoading ? <Spinner /> : (
        <Table head={["Titlu", "Adresă", "Conținut", "Actualizată", ""]} empty={list.data?.data.length === 0}>
          {list.data?.data.map((p) => (
            <tr key={p.id} className="hover:bg-slate-50">
              <td className="px-4 py-3">
                <Link to={`/pages/${p.id}`} className="font-semibold hover:text-brand">{p.title}</Link>
                {p.kind !== "custom" && <span className="ml-2"><Badge tone="violet">legală</Badge></span>}
                {!p.published && <span className="ml-2"><Badge>ascunsă</Badge></span>}
              </td>
              <td className="px-4 py-3 font-mono text-xs text-slate-500">/pagina/{p.slug}</td>
              <td className="px-4 py-3">{p.is_template ? <Badge tone="amber">model platformă</Badge> : <Badge tone="green">editată</Badge>}</td>
              <td className="px-4 py-3 text-sm text-slate-500">{date(p.updated_at)}</td>
              <td className="px-4 py-3 text-right">
                {p.published && url(p.slug) && <a href={url(p.slug)} target="_blank" rel="noopener" className="text-sm text-brand hover:underline">Vezi ↗</a>}
              </td>
            </tr>
          ))}
        </Table>
      )}
      <p className="mt-4 text-xs text-slate-400">
        Paginile marcate „model platformă” folosesc textul pregătit de shopcore, completat cu datele firmei tale.
        Modelele sunt orientative: verifică-le cu un jurist și adaptează-le activității tale.
      </p>
    </>
  );
}

export function PageEdit() {
  const { id } = useParams();
  const isNew = !id;
  const nav = useNavigate();
  const qc = useQueryClient();
  const canEdit = useCanEdit();
  const url = useShopUrl();
  const detail = useQuery({ queryKey: ["page", id], queryFn: () => admin.get<StorePageDetail>(`/pages/${id}`), enabled: !isNew });
  const store = useQuery({ queryKey: ["store"], queryFn: () => admin.get<Store>("/store"), enabled: isNew });
  const [f, setF] = useState({ title: "", slug: "", body: "", published: true });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string }>();
  const [rev, setRev] = useState<PageRevision | null>(null);
  const d = detail.data;
  useEffect(() => {
    if (d) setF({ title: d.title, slug: d.slug, body: d.body, published: d.published });
  }, [d]);

  const onError = (e: unknown) => {
    setErrors(e instanceof ApiError ? e.problem.errors ?? {} : {});
    setMsg({ ok: false, text: errorMessage(e) });
  };
  const done = (text: string) => {
    qc.invalidateQueries({ queryKey: ["pages"] });
    qc.invalidateQueries({ queryKey: ["page", id] });
    setErrors({});
    setMsg({ ok: true, text });
  };
  const save = useMutation({
    mutationFn: () => isNew
      ? admin.post<StorePage>("/pages", { title: f.title, slug: f.slug || undefined, body: f.body, published: f.published })
      : admin.patch<StorePage>(`/pages/${id}`, d?.kind === "custom" ? f : { title: f.title, body: f.body }),
    onSuccess: (p) => { done("Pagina a fost salvată."); if (isNew) nav(`/pages/${p.id}`, { replace: true }); },
    onError,
  });
  const reset = useMutation({ mutationFn: () => admin.post<StorePage>(`/pages/${id}/reset`), onSuccess: () => done("Pagina folosește din nou modelul platformei."), onError });
  const del = useMutation({
    mutationFn: () => admin.del(`/pages/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pages"] }); nav("/pages"); },
    onError,
  });

  if (!isNew && detail.isLoading) return <Spinner />;
  if (!isNew && !d) return <Alert>{errorMessage(detail.error)}</Alert>;
  const legal = !!d && d.kind !== "custom";
  const placeholders = d?.placeholders ?? placeholdersFromStore(store.data);
  const dirty = !d || f.title !== d.title || f.body !== d.body || f.slug !== d.slug || f.published !== d.published;

  return (
    <>
      <PageHeader
        title={isNew ? "Pagină nouă" : d!.title}
        subtitle={<Link to="/pages" className="hover:underline">← Toate paginile</Link>}
        actions={!isNew && d!.published && url(d!.slug) && <a href={url(d!.slug)} target="_blank" rel="noopener"><Button variant="secondary">Vezi în magazin ↗</Button></a>}
      />
      <CompanyNotice />
      {legal && (
        <div className="mb-6">
          <Alert tone="amber">
            {d!.is_template
              ? "Pagina folosește modelul platformei: un text orientativ, completat automat cu datele firmei. Verifică-l cu un jurist și adaptează-l activității tale; la prima salvare textul devine al tău."
              : "Pagina legală are adresă fixă (checkout-ul trimite clienții aici) și rămâne mereu publicată. Fiecare salvare păstrează o revizie, iar comenzile rețin versiunea acceptată."}
          </Alert>
        </div>
      )}
      <form className="grid gap-6 xl:grid-cols-2" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Card title="Conținut">
          <div className="space-y-4">
            <Field label="Titlu" error={errors.title}>
              <input className="input" required maxLength={120} disabled={!canEdit} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
            </Field>
            {!legal && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Adresă" hint={isNew ? "Opțional: se generează din titlu." : undefined} error={errors.slug}>
                  <div className="flex items-center gap-1 text-sm text-slate-400">/pagina/
                    <input className="input font-mono" disabled={!canEdit} placeholder="despre-noi" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} />
                  </div>
                </Field>
                <div className="pt-6"><Toggle checked={f.published} onChange={(v) => canEdit && setF({ ...f, published: v })} label="Publicată (apare în subsol)" /></div>
              </div>
            )}
            <Field label="Text (Markdown)" error={errors.body}>
              <textarea className="input min-h-[28rem] font-mono text-xs leading-relaxed" disabled={!canEdit} value={f.body}
                onChange={(e) => setF({ ...f, body: e.target.value })} />
            </Field>
            <details className="text-xs text-slate-500">
              <summary className="cursor-pointer font-semibold">Formatare și câmpuri automate</summary>
              <div className="mt-2 space-y-2">
                <p><code>## Titlu</code> · <code>**îngroșat**</code> · <code>- listă</code> · <code>1. listă numerotată</code> · <code>[text](https://adresa)</code> · <code>[pagină](/pagina/politica-de-retur)</code></p>
                <p>Câmpuri completate automat: {PLACEHOLDERS.map(([k, l]) => <span key={k} className="mr-2 whitespace-nowrap"><code>{`{{${k}}}`}</code> {l}</span>)}</p>
              </div>
            </details>
            {msg && <Alert tone={msg.ok ? "green" : "red"}>{msg.text}</Alert>}
            {canEdit && (
              <div className="flex flex-wrap items-center gap-2">
                <Button disabled={save.isPending || !dirty}>{save.isPending ? "Se salvează…" : "Salvează"}</Button>
                {legal && !d!.is_template && (
                  <Button type="button" variant="secondary" disabled={reset.isPending}
                    onClick={() => confirm("Revii la modelul platformei? Textul tău rămâne în istoric.") && reset.mutate()}>Revino la model</Button>
                )}
                {!isNew && !legal && (
                  <Button type="button" variant="ghost" className="ml-auto text-red-600" onClick={() => confirm(`Ștergi pagina „${d!.title}”?`) && del.mutate()}>Șterge</Button>
                )}
              </div>
            )}
          </div>
        </Card>
        <div className="space-y-6">
          <Card title="Previzualizare">
            <h1 className="mb-4 text-2xl font-extrabold">{f.title}</h1>
            <Markdown>{fill(f.body, placeholders)}</Markdown>
          </Card>
          {d && (
            <Card title="Istoric">
              {d.revisions.length === 0 ? <p className="text-sm text-slate-400">{d.is_template ? "Modelul platformei, needitat." : "Nicio revizie."}</p> : (
                <ul className="divide-y divide-slate-100 text-sm">
                  {d.revisions.map((r) => (
                    <li key={r.id} className="flex items-center justify-between py-2">
                      <span>
                        {date(r.created_at)} <span className="text-slate-400">· {r.author || "echipa platformei"}</span>
                        {r.id === d.revision_id && <span className="ml-2"><Badge tone="green">curentă</Badge></span>}
                        <code className="ml-2 text-xs text-slate-400">{r.id.slice(-8)}</code>
                      </span>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setRev(r)}>Vezi</Button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
        </div>
      </form>
      <RevisionModal pageId={id} rev={rev} onClose={() => setRev(null)} onRestore={canEdit ? (title, body) => { setF({ ...f, title, body }); setRev(null); setMsg(undefined); } : undefined} />
    </>
  );
}

function RevisionModal({ pageId, rev, onClose, onRestore }: {
  pageId?: string; rev: PageRevision | null; onClose: () => void; onRestore?: (title: string, body: string) => void;
}) {
  const q = useQuery({
    queryKey: ["page-revision", rev?.id],
    queryFn: () => admin.get<PageRevision & { body: string }>(`/pages/${pageId}/revisions/${rev!.id}`),
    enabled: !!rev,
  });
  return (
    <Modal open={!!rev} onClose={onClose} title={rev ? `Revizia din ${date(rev.created_at)}` : ""}>
      {q.data ? (
        <div className="space-y-4">
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-xs">{q.data.body}</pre>
          {onRestore && (
            <div className="flex justify-end">
              <Button onClick={() => onRestore(q.data.title, q.data.body)}>Încarcă în editor</Button>
            </div>
          )}
        </div>
      ) : <Spinner />}
    </Modal>
  );
}

function placeholdersFromStore(s?: Store): Record<string, string> {
  const c = (s?.settings.company ?? {}) as Company;
  const v = (x?: string) => x?.trim() || "[necompletat]";
  return { magazin: s?.name ?? "", firma: v(c.legal_name), cui: v(c.vat_number), reg_com: v(c.reg_number), sediu: v(c.address), email: v(c.email), telefon: v(c.phone) };
}
